import * as React from "react";
import { Fieldset } from "@trussworks/react-uswds";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { AnalyticsAction, logEvent } from "../../../utils/analytics";
import { 
  SearchFilters,
 } from "../../../types/v1_schemas";
import FilterCheckbox from "./FilterCheckbox";
import AppAlert from "../../AppAlert";
import { useTranslation } from "react-i18next";
import { ReactComponent as Phone } from "../../../images/phone.svg";
import { toggleItemsInFilterList } from "../../../utils/filters/shared";
import ReactMarkdown from "react-markdown";
import { AxiosResponse } from "axios";
import i18n from "../../../i18n";
import { QueryLookupTablesApi } from "../../../utils/QueryHelper";

const serviceMatch = ["Screening and early prevention for mental health", "Detección y prevención temprana de problemas de salud mental",
        "Walk-in crisis services", "Servicios sin cita previa en caso de crisis",
        "Withdrawal management (i.e. detox)", "Manejo de la abstinencia (es decir, desintoxicación)", 
        "Opioid use disorder treatment", "Tratamiento de trastornos por consumo de opioides",
        "Residential for mental health", "Residencias para la salud mental",
        "Residential for substance use", "Residencias por el consumo de sustancias",
        "DUI Level I/II Education and Therapy", "Educación y terapia de nivel I/II por DUI"];

type ApiServiceProvided = {
  id: number;
  categoryId: number;
  plainLanguageEn: string;
  plainLanguageEs: string;
  textEn: string;
  textEs: string;
};

export type ResponseServiceProvided = {
  id: string;
  categoryId: number;
  plainLanguage: string;
  text: string;
};

type TypeOfHelpInputProps = {
  legend: string;
  compact?: boolean;
  filters: SearchFilters;
  setFilters: Dispatch<SetStateAction<SearchFilters>>;
};

function TypeOfHelpInput({
  legend,
  compact = false,
  filters,
  setFilters,
}: TypeOfHelpInputProps) {
  const { t } = useTranslation();
  const [responseProviders, setResponseProviders] = useState(new Array<ResponseServiceProvided>());

  const responseFn = (response: AxiosResponse) => {
    setResponseProviders(
      response.data?.servicesProvided.reduce((acc: ResponseServiceProvided[], service: ApiServiceProvided) => {
        let index = -1;
        if (!acc || acc.length > 0)
          index = acc.findIndex(s => s.text === (i18n.language === "es" ? service.textEs : service.textEn));
        if (index === -1) {
          acc.push({
            id: service.id.toString(),
            categoryId: service.categoryId,
            plainLanguage: i18n.language === "es" ? service.plainLanguageEs : service.plainLanguageEn,
            text: i18n.language === "es" ? service.textEs : service.textEn
          });
          return acc;
        } else {
          acc[index].id += `,${service.id.toString()}`;
          return acc;
        }
      }, []));
  }

  const errorFn = (error: any) => {
    console.error("Error fetching services provided:", error);
  }
  
  useEffect(() => {
    QueryLookupTablesApi(
      {
        IncludeServicesProvided: true,
        language: i18n.language
      },
      new AbortController().signal,
      responseFn, errorFn);
  }, [i18n.language]);
  
  const setServiceProvidedFilter = (serviceProvided: ResponseServiceProvided) => {
    logEvent(AnalyticsAction.UpdateFilter, {
      label: "type of help",
      filter_type: "type of help",
      filter_value: serviceProvided.text,
    });
    setFilters({
      ...filters,
      serviceProvidedIds: toggleItemsInFilterList(
        filters.serviceProvidedIds as string[], 
        serviceProvided.id.toString().split(",")
        ) as [],
    });
  };

  return (
    <Fieldset legend={legend} legendStyle={compact ? "srOnly" : "large"}>
      {responseProviders.filter(service => serviceMatch.includes(service.text)).sort((a, b) => a.text.localeCompare(b.text)).map((option: ResponseServiceProvided) => (
        <React.Fragment key={option.plainLanguage}>
          <FilterCheckbox
            name={option.id.toString()}
            value={option.id.toString()}
            label={option.text}
            customChecked={() => {return (filters.serviceProvidedIds ?? []).some(id => option.id.split(",").includes(id))}}
            selectedFilterValues={filters.serviceProvidedIds ?? []}
            onChange={() => setServiceProvidedFilter(option)}
            key={option.id.toString()}
          />

          {(option.text === "Walk-in crisis services" || option.text === "Servicios sin cita previa en caso de crisis") && 
            option.id.split(",").some(id => filters.serviceProvidedIds?.includes(id)) && (
              <div className="margin-y-2" id="crisis-content-box">
                <AppAlert Icon={Phone}>
                  <ReactMarkdown>{t("suicidalIdeationAlert")}</ReactMarkdown>
                </AppAlert>
              </div>
            )}
        </React.Fragment>
      ))}
    </Fieldset>
  );
}

export default TypeOfHelpInput;
